/**
 * React Query hooks for messages
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMessage,
  getMessageContext,
  getManuscriptChats,
  getMessageWindow,
  updateChat,
} from '@/api/messages';
import {
  Message,
  MessageContextResponse,
  MessageWindowParams,
  MessageWindowResponse,
  Chat,
} from '@/types/api';
import { getErrorMessage } from '@/api/client';

/**
 * Hook to fetch single message
 */
export const useMessage = (id: number) => {
  return useQuery<Message, Error>({
    queryKey: ['messages', id],
    queryFn: () => getMessage(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch message context
 */
export const useMessageContext = (
  chatId?: number | null,
  messageId?: number | null,
  contextSize: number = 5
) => {
  return useQuery<MessageContextResponse, Error>({
    queryKey: ['messages', chatId, messageId, 'context', contextSize],
    queryFn: () => {
      if (!chatId || !messageId) {
        throw new Error('Chat ID and message ID are required');
      }
      return getMessageContext(chatId, messageId, contextSize);
    },
    enabled: Boolean(chatId && messageId),
  });
};

/**
 * Hook to fetch message windows on demand
 */
export const useMessageWindow = () => {
  return useMutation<MessageWindowResponse, Error, MessageWindowParams>({
    mutationFn: getMessageWindow,
    onError: (error) => {
      console.error('Failed to fetch message window:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to fetch manuscript-only chats
 */
export const useManuscriptChats = () => {
  return useQuery<Chat[], Error>({
    queryKey: ['chats', 'manuscript'],
    queryFn: getManuscriptChats,
  });
};

/**
 * Hook to update a chat
 */
export const useUpdateChat = () => {
  const queryClient = useQueryClient();

  return useMutation<Chat, Error, { id: number; is_manuscript_channel: boolean }>({
    mutationFn: ({ id, is_manuscript_channel }) =>
      updateChat(id, { is_manuscript_channel }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['chats', 'manuscript'] });
      // The Channels page reads the annotated list, and is_manuscript_channel
      // is one of its filter dimensions — it has to refetch too.
      queryClient.invalidateQueries({ queryKey: ['chats-with-annotations'] });
    },
    onError: (error) => {
      console.error('Failed to update chat:', getErrorMessage(error));
    },
  });
};
